import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, Image as ImageIcon, Loader2, X, Eye, EyeOff, Link as LinkIcon } from 'lucide-react';
import { API, adminAuthHeader, resolveImg } from '../api';
import ImageUploader from './ImageUploader';
import AdminSearch from './AdminSearch';

const emptyForm = { title: '', subtitle: '', image: '', link_url: '', is_active: true, order: 0, ribbon_enabled: false, ribbon_text: 'Limited Time' };

const BannerManager = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/banners`, { headers: adminAuthHeader() });
      setItems(res.data || []);
    } catch (_) {
      setItems([]);
    } finally { setLoading(false); }
  };

  const start = (b) => {
    setErr('');
    if (b) { setEditing(b.id); setForm({ ...emptyForm, ...b }); }
    else { setEditing('new'); setForm({ ...emptyForm, order: items.length }); }
  };
  const cancel = () => { setEditing(null); setForm(emptyForm); setErr(''); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setErr('');
    if (!form.image) return setErr('Please upload a banner image');
    const payload = { ...form, order: Number(form.order) || 0 };
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/banners`, payload, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/banners/${editing}`, payload, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete banner "${title || 'Untitled'}"?`)) return;
    try {
      await axios.delete(`${API}/admin/banners/${id}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  const toggleActive = async (b) => {
    try {
      await axios.put(`${API}/admin/banners/${b.id}`, { ...b, is_active: !b.is_active }, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  return (
    <div data-testid="banner-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Offer Banners</h2>
          <p className="text-sm text-gray-500">These promotional banners appear as a sliding carousel above the Bestsellers section on the home page.</p>
        </div>
        <button
          onClick={() => start(null)}
          data-testid="banner-add-btn"
          className="flex items-center gap-1.5 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md hover:bg-[#0a548e]"
        >
          <Plus size={14} /> Add Banner
        </button>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search title, subtitle, ribbon..." testId="banner-search" />
      </div>


      {editing !== null && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5" data-testid="banner-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              {editing === 'new' ? 'New banner' : `Editing "${form.title || 'Untitled'}"`}
            </h3>
            <button onClick={cancel} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title <span className="text-gray-400">(optional overlay)</span></label>
                <input value={form.title} onChange={(e) => upd('title', e.target.value)} placeholder="e.g. Summer Getaway Sale" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="banner-title-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subtitle <span className="text-gray-400">(optional)</span></label>
                <input value={form.subtitle} onChange={(e) => upd('subtitle', e.target.value)} placeholder="e.g. Up to 30% off on Bali packages" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="banner-subtitle-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Link URL <span className="text-gray-400">(optional)</span></label>
                <input value={form.link_url} onChange={(e) => upd('link_url', e.target.value)} placeholder="e.g. /packages/118575 or https://…" className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="banner-link-input" />
                <p className="text-[11px] text-gray-400 mt-1">Clicking the banner will navigate here. Leave blank for a non-clickable banner.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Order</label>
                  <input type="number" value={form.order} onChange={(e) => upd('order', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" data-testid="banner-order-input" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none pb-2">
                    <input type="checkbox" checked={!!form.is_active} onChange={(e) => upd('is_active', e.target.checked)} className="accent-[#0d64a8]" data-testid="banner-active-toggle" />
                    Active (visible on site)
                  </label>
                </div>
              </div>
              <div className="border border-dashed border-gray-200 rounded-md p-3 bg-[#fffdf7]">
                <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.ribbon_enabled} onChange={(e) => upd('ribbon_enabled', e.target.checked)} className="accent-[#e11d48]" data-testid="banner-ribbon-toggle" />
                  <span className="font-medium">Show &ldquo;Limited Time&rdquo; ribbon</span>
                </label>
                <p className="text-[11px] text-gray-500 mt-1 ml-6">A red corner ribbon appears on the banner to boost urgency.</p>
                {form.ribbon_enabled && (
                  <div className="mt-2 ml-6">
                    <label className="block text-[11px] font-medium text-gray-600 mb-1">Ribbon Text</label>
                    <input
                      value={form.ribbon_text || ''}
                      onChange={(e) => upd('ribbon_text', e.target.value)}
                      placeholder="Limited Time"
                      maxLength={24}
                      className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:border-[#0d64a8]"
                      data-testid="banner-ribbon-text"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Keep it short (max ~20 chars) so it fits the ribbon.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <ImageUploader value={form.image} onChange={(url) => upd('image', url)} label="Banner Image" hint="1600×500 works best. Landscape orientation, promotional artwork." height="h-52" />
            </div>
          </div>
          {err && <div className="mt-3 bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={cancel} className="text-sm text-gray-600 px-4 py-2 border border-gray-200 rounded-md">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="banner-save-btn" className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-2 rounded-md flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14} />} {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-gray-500 py-10">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <ImageIcon className="mx-auto text-gray-300 mb-2" size={38}/>
          <p className="text-gray-700 font-medium">No banners yet</p>
          <p className="text-xs text-gray-500 mt-1">Add a banner to feature offers on the home page.</p>
          <button onClick={() => start(null)} className="mt-3 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md">Add your first banner</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.filter((b) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            return [b.title, b.subtitle, b.ribbon_text, b.link_url].filter(Boolean).join(' ').toLowerCase().includes(s);
          }).map((b) => (
            <div key={b.id} data-testid={`banner-card-${b.id}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col hover:shadow-sm transition">
              <div className="aspect-[16/6] bg-gray-100 relative">
                {b.image ? (
                  <img src={resolveImg(b.image)} alt={b.title || 'banner'} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-300"><ImageIcon size={32}/></div>
                )}
                <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${b.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                  {b.is_active ? 'Active' : 'Hidden'}
                </span>
                {b.ribbon_enabled && (
                  <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border bg-rose-50 text-rose-700 border-rose-200">
                    Ribbon: {b.ribbon_text || 'Limited Time'}
                  </span>
                )}
                <span className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] text-gray-700 border border-white/60">
                  Order {b.order}
                </span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-semibold text-sm text-gray-900 truncate">{b.title || <span className="text-gray-400 font-normal italic">Untitled banner</span>}</div>
                {b.subtitle && <div className="text-xs text-gray-500 truncate mt-0.5">{b.subtitle}</div>}
                {b.link_url && (
                  <a href={b.link_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#0d64a8] hover:underline mt-1 flex items-center gap-1 truncate">
                    <LinkIcon size={10}/> {b.link_url}
                  </a>
                )}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3">
                  <button
                    onClick={() => toggleActive(b)}
                    data-testid={`banner-toggle-${b.id}`}
                    className="text-[11px] text-gray-600 hover:text-[#0d64a8] flex items-center gap-1"
                  >
                    {b.is_active ? <><EyeOff size={12}/> Hide</> : <><Eye size={12}/> Show</>}
                  </button>
                  <button onClick={() => start(b)} data-testid={`banner-edit-${b.id}`} className="text-[11px] text-[#0d64a8] hover:underline">Edit</button>
                  <button onClick={() => remove(b.id, b.title)} data-testid={`banner-delete-${b.id}`} className="text-[11px] text-red-500 hover:underline ml-auto flex items-center gap-1"><Trash2 size={11}/> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerManager;
